import {
  Component,
  inject,
  Input,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { Patient } from '../../models/patient';
import { Organisme } from '../../models/organisme';
import { User } from '../../models/user';
import { PatientService } from '../../services/patient.service';
import { OrganismeService } from '../../services/organisme.service';
import { AuthService } from '../../services/auth.service';
import { TuiButton, TuiSurface } from '@taiga-ui/core';
import { TuiCardLarge } from '@taiga-ui/layout';
import { PrescriptionFormService } from '../../services/prescription-form.service';
import {
  PrescriptionForm,
  PrescriptionFormList,
} from '../../models/prescriptionForm/prescriptionForm';
import { PrescriptionRowComponent } from '../prescription-row/prescription-row.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TuiPdfViewerOptions, TuiPdfViewerService } from '@taiga-ui/kit';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { EventEmitter } from '@angular/core';
import { CodeActiviteService } from '../../services/code-activite.service';

@Component({
  selector: 'app-prescription-list-card',
  standalone: true,
  imports: [TuiCardLarge, TuiSurface, TuiButton, PrescriptionRowComponent],
  templateUrl: './prescription-list-card.component.html',
  styleUrl: './prescription-list-card.component.scss',
})
export class PrescriptionListCardComponent {
  private readonly pdfService = inject(TuiPdfViewerService);
  private readonly sanitizer = inject(DomSanitizer);

  private patientService = inject(PatientService);
  private organismeService = inject(OrganismeService);
  private authService = inject(AuthService);
  private prescriptionFormService = inject(PrescriptionFormService);
  protected codeActiviteService = inject(CodeActiviteService);

  @Input() patient?: Patient;
  @Input({ required: true }) activite!: string;

  @Output() formCompleted = new EventEmitter<void>();

  public userList: WritableSignal<User[] | undefined> = signal(undefined);
  public prescriptionFormList: WritableSignal<
    PrescriptionFormList | undefined
  > = signal(undefined);
  public organisme: WritableSignal<Organisme | undefined | null> =
    signal(undefined);

  ngOnInit(): void {
    if (this.patient && this.activite) {
      this.prescriptionFormService.pullList(this.patient.nir).subscribe({
        next: (prescriptionList) => {
          this.prescriptionFormList.set(prescriptionList);
        },
      });
      this.patientService.pullUserList(this.patient!).subscribe({
        next: (userList) => {
          this.userList.set(
            this.authService.user?.role?.slug === 'admin' ||
              this.authService.user?.role?.slug === 'super-admin'
              ? userList.list
              : userList.list.filter((user) =>
                  this.authService
                    .getUser()
                    ?.autorisations?.get('prescribe')
                    ?.includes(user.id),
                ),
          );
        },
      });
      if (this.patient.assure?.numeroOrganisme) {
        this.organismeService
          .pullDetail(this.patient.assure.numeroOrganisme)
          .subscribe({
            next: (data) => {
              this.organisme.set(data ?? null);
            },
            error: (error) => {
              this.organisme.set(null);
            },
          });
      } else {
        this.organisme.set(null);
      }
    }
  }

  // bouton à afficher pendant un an et 2 mois à compter du 23 juin 2026 le temps que tout les patients soient renouvellés
  afficherBoutonTemporaire(): boolean {
    const dateReference = new Date(2026, 5, 22);
    const dateLimite = new Date(2027, 7, 22);
    if (new Date() > dateLimite) {
      return false;
    } else {
      if (this.prescriptionFormList()?.list) {
        for (const prescription of this.prescriptionFormList()!.list) {
          if (prescription.dateDePrescription > dateReference) {
            return false;
          }
        }
      }
      return true;
    }
  }

  dernierePrescriptionForm(
    codeDActivite: 'PPC' | 'O2' | 'VENTILATION' | 'AVF' | 'AEROSOLS' | 'O2VNI',
  ): PrescriptionForm | undefined {
    const forms = this.prescriptionFormList()?.list || [];

    const activityForms = forms.filter(
      (form) => form.codeDActivite === codeDActivite,
    );

    // Retourne la prescription la plus ancienne de l'activité
    return activityForms.length > 0
      ? activityForms.reduce((prev, current) =>
          current.dateDePrescription < prev.dateDePrescription ? prev : current,
        )
      : undefined;
  }

  openPdf(
    actions: PolymorpheusContent<TuiPdfViewerOptions>,
    prescriptionForm: PrescriptionForm,
  ): void {
    try {
      if (!prescriptionForm.id) {
        return;
      }

      this.prescriptionFormService.pdf(prescriptionForm.id).subscribe({
        next: (blob) => {
          const url: SafeResourceUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(blob),
            );

          this.pdfService
            .open(url, {
              label: 'Dernière prescription',
              actions,
            })
            .subscribe();
        },
      });
    } catch (error) {
      console.error(
        "Erreur lors de l'ouverture de la dernière prescription : ",
        error,
      );
    }
  }

  onFormCompleted(): void {
    this.formCompleted.emit();
  }
}
